/**
 * Pure drag resolution for the track sidebar, kept out of Tracks.tsx like
 * block-drag.ts is kept out of BlockPanel.
 */
import { arrayMove } from '@dnd-kit/sortable';
import { isEligibleMoveDestination } from './sub-track-move';
import { TrackPreview } from './types';

// Rows the drag in progress took out of play: 'children' while a parent is
// dragged, 'parents' while a sub-track is (which only rules out its own
// parent row).
export type DisabledTracks = 'children' | 'parents' | null;

/**
 * The drop-zone highlight for a row during a cross-parent sub-track drag, or
 * null. Same-group reorders have their own feedback, so they get none;
 * 'ineligible' previews the name-collision check the modal will run.
 */
export function resolveDropHighlight({
  track,
  items,
  trackToSort,
  overGroupId,
  moveSubTrackEnabled,
}: {
  track: TrackPreview;
  items: TrackPreview[];
  trackToSort: TrackPreview | null;
  overGroupId: string | null;
  moveSubTrackEnabled: boolean;
}): 'eligible' | 'ineligible' | null {
  if (
    !trackToSort?.parentTrackId ||
    !moveSubTrackEnabled ||
    !overGroupId ||
    overGroupId === trackToSort.parentTrackId ||
    !(track.id === overGroupId || track.parentTrackId === overGroupId)
  ) {
    return null;
  }

  // Require a parent track: a same-group hover resolves to a sub-track id,
  // which is never a destination and would read as 'ineligible'.
  const destination = items.find(
    (t) => t.id === overGroupId && t.parentTrackId == null,
  );
  if (!destination) return null;

  return isEligibleMoveDestination(
    trackToSort,
    trackToSort.parentTrackId,
    destination,
    items,
  )
    ? 'eligible'
    : 'ineligible';
}

/**
 * Resolves the row id under the pointer to the id the drag counts as over, or
 * null when that row is not a valid target. A parent and its sub-tracks form
 * one drop zone, so a member of another group resolves to that parent; with
 * the feature off, a sub-track drag stays confined to its own group.
 */
export function resolveTrackCollision({
  candidateId,
  items,
  disabledTracks,
  trackToSort,
  moveSubTrackEnabled,
}: {
  candidateId: string | null | undefined;
  items: TrackPreview[];
  disabledTracks: DisabledTracks;
  trackToSort: TrackPreview | null;
  moveSubTrackEnabled: boolean;
}): string | null {
  const candidate = items.find((t) => t.id === candidateId);
  if (!candidate) return null;

  if (disabledTracks === 'children') {
    return candidate.parentTrackId ? null : candidate.id;
  }

  if (disabledTracks === 'parents') {
    // Feature off: confine the drag to its own group, as before the feature.
    if (!moveSubTrackEnabled) {
      return candidate.parentTrackId === trackToSort?.parentTrackId
        ? candidate.id
        : null;
    }

    if (!candidate.parentTrackId) {
      return candidate.id === trackToSort?.parentTrackId ? null : candidate.id;
    }

    return candidate.parentTrackId === trackToSort?.parentTrackId
      ? candidate.id
      : candidate.parentTrackId;
  }

  // No drag in progress — e.g. the drag-start state has not flushed yet — so
  // every track is a target and collision detection behaves as if unfiltered.
  return candidate.id;
}

export type TrackDropOutcome =
  | { kind: 'move'; subTrack: TrackPreview; destinationTrack: TrackPreview }
  | { kind: 'reorder'; tracks: TrackPreview[] }
  | { kind: 'none' };

/**
 * What a finished drag does: a sub-track dropped on a different parent opens
 * the move flow (unchanged until the coach confirms); everything else is a
 * same-group reorder.
 */
export function resolveTrackDrop({
  items,
  activeId,
  overId,
  moveSubTrackEnabled,
}: {
  items: TrackPreview[];
  activeId: string;
  overId: string | null;
  moveSubTrackEnabled: boolean;
}): TrackDropOutcome {
  if (!overId || activeId === overId) return { kind: 'none' };

  const activeTrack = items.find((t) => t.id === activeId);
  const overTrack = items.find((t) => t.id === overId);

  if (!activeTrack || !overTrack) return { kind: 'none' };

  // Defence in depth: collision detection withholds this row, but falling
  // through would fire a no-op reorder write.
  if (overTrack.id === activeTrack.parentTrackId) return { kind: 'none' };

  const isCrossParentDrop =
    activeTrack.parentTrackId != null &&
    overTrack.parentTrackId == null &&
    overTrack.id !== activeTrack.parentTrackId;

  if (moveSubTrackEnabled && isCrossParentDrop) {
    return {
      kind: 'move',
      subTrack: activeTrack,
      destinationTrack: overTrack,
    };
  }

  return { kind: 'reorder', tracks: reorderTracks(items, activeId, overId) };
}

export function reorderTracks(
  items: TrackPreview[],
  activeId: string,
  overId: string | null,
): TrackPreview[] {
  if (!overId || activeId === overId) return items;

  const activeTrack = items.find((t) => t.id === activeId);
  const overTrack = items.find((t) => t.id === overId);

  if (!activeTrack || !overTrack) return items;

  // Only allow sorting within the same group
  if (activeTrack.parentTrackId !== overTrack.parentTrackId) {
    return items;
  }

  const orderedTracks = [...items];
  const currentGroup = orderedTracks.filter(
    (track) => track.parentTrackId === activeTrack.parentTrackId,
  );

  const oldIndexInGroup = currentGroup.findIndex((t) => t.id === activeId);
  const newIndexInGroup = currentGroup.findIndex((t) => t.id === overId);

  // Must stay pure: the overlay re-sorts the rendered list, so a sortOrder
  // written onto a base track would outlive a failed drag's clear.
  const reorderedGroup = arrayMove(
    currentGroup,
    oldIndexInGroup,
    newIndexInGroup,
  ).map((track, index) => ({ ...track, sortOrder: index + 1 }));

  return orderedTracks.map((track) => {
    if (track.parentTrackId === activeTrack.parentTrackId) {
      return reorderedGroup.find((t) => t.id === track.id) || track;
    }
    return track;
  });
}
